import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid, Typography } from "@mui/material";
import { t } from "i18next";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { COMPANY_ROLE_CHOICES } from "../../../helpers/constants";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import i18n from "../../../i18n";
import BasicDialog from "../../base/BasicDialog";
import { SearchOtherClientButton } from "../../button/SearchOtherClientButton/SearchOtherClientButton";
import { ControlledSelect } from "../../form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../form/ControlledTextField/ControlledTextField";
import { SearchOtherClientDialog } from "../ClientDiscoveryDialog/SearchOtherClientDialog";

// TODO validate if existingClient is false even when existingClientId is provided
export const connectedCompanyPersonFormSchema = z.object({
    existingClient: z.boolean(),
    exisitngClientPersonId: z.string().optional().nullable(),
    existingClientId: z.string().optional().nullable(),

    name: z.string().optional(),
    surname: z.string().optional(),
    companyFunction: z
        .string()
        .min(1, { message: t("clients.AddConnectedCompanyPerson.roleRequired") }),
    phone: z
        .string()
        .nullable()
        .optional()
        .refine(
            (val) => {
                if (!val || val === "") {
                    return true;
                }

                const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
                return phoneRegex.test(val.replace(/[\s\-\(\)]/g, ""));
            },
            {
                message: t("clients.AddConnectedCompanyPerson.invalidPhone"),
            },
        ),
    email: z
        .string()
        .nullable()
        .optional()
        .refine(
            (val) => {
                if (!val || val === "") {
                    return true;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(val);
            },
            {
                message: t("clients.AddConnectedCompanyPerson.invalidEmail"),
            },
        ),
});

export function AddConnectedCompanyPerson(props) {
    const { handleSubmit, control, formState, register, reset, setValue } = useForm({
        resolver: zodResolver(connectedCompanyPersonFormSchema),
        defaultValues: {
            existingClient: false,
            phone: "",
            email: "",
            name: "",
            surname: "",
            companyFunction: "",
        },
    });

    const existingClient = useWatch({
        control,
        name: "existingClient",
    });

    const { closeDialog: closeSearchOtherClientDialog, openDialog: openSearchOtherClientDialog } =
        useDialogBetter();

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("clients.AddConnectedCompanyPerson.title")}
            maxWidth={"sm"}
            showDialogActions
            titleSx={{
                pb: 0,
            }}
            headerComponent={
                <Typography
                    variant={"subtitle1"}
                    sx={{ color: "text.secondary", mx: "auto", mb: 1 }}
                >
                    {t("clients.AddConnectedCompanyPerson.selectPerson")}
                </Typography>
            }
        >
            <Grid container direction={"column"}>
                <Grid container>
                    <Grid
                        container
                        sx={{ mt: 0 }}
                        direction={"row"}
                        justifyContent={"start"}
                        alignItems={"center"}
                        spacing={2}
                    >
                        <Grid item xs={6}>
                            <ControlledSelect
                                label={t("clients.AddConnectedCompanyPerson.clientExists")}
                                control={control}
                                name="existingClient"
                                data={[
                                    {
                                        label: t("clients.AddConnectedCompanyPerson.yes"),
                                        value: true,
                                    },
                                    {
                                        label: t("clients.AddConnectedCompanyPerson.no"),
                                        value: false,
                                    },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <SearchOtherClientButton
                                fullWidth
                                onClick={() => {
                                    openSearchOtherClientDialog({
                                        Component: SearchOtherClientDialog,
                                        componentProps: {
                                            title: t("clients.ClientSearchTable.searchClient"),
                                            excludedCustomerIds: props.excludedCustomerIds,
                                            onCustomerSelected: (customer) => {
                                                console.log(customer);
                                                closeSearchOtherClientDialog();
                                                setValue(
                                                    "email",
                                                    customer.person?.email.email_address || "",
                                                );
                                                setValue("name", customer.person?.name || "");
                                                setValue("surname", customer.person?.surname || "");
                                                setValue(
                                                    "phone",
                                                    customer.person?.phone.phone_number || "",
                                                );
                                                setValue(
                                                    "exisitngClientPersonId",
                                                    customer.person?.id,
                                                );
                                                setValue("existingClientId", customer.id);
                                            },
                                        },
                                    });
                                }}
                                disabled={!existingClient}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction={"column"}
                        sx={{ mt: 3 }}
                        gap={1}
                        justifyContent={"center"}
                    >
                        <Grid container justifyContent={"center"}>
                            <Grid item xs={6}>
                                <ControlledTextField
                                    control={control}
                                    name="name"
                                    label={t("clients.AddConnectedCompanyPerson.firstName")}
                                    props={{
                                        fullWidth: true,
                                        size: "small",
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container justifyContent={"center"}>
                            <Grid item xs={6}>
                                <ControlledTextField
                                    control={control}
                                    name="surname"
                                    label={t("clients.AddConnectedCompanyPerson.lastName")}
                                    props={{
                                        fullWidth: true,
                                        size: "small",
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container justifyContent={"center"}>
                            <Grid item xs={6}>
                                <ControlledSelect
                                    control={control}
                                    data={COMPANY_ROLE_CHOICES.map((role) => ({
                                        label: i18n.t(`company.roles.${role}`),
                                        value: role,
                                    }))}
                                    label={t("clients.AddConnectedCompanyPerson.role")}
                                    name="companyFunction"
                                />
                            </Grid>
                        </Grid>
                        <Grid container justifyContent={"center"}>
                            <Grid item xs={6}>
                                <ControlledTextField
                                    control={control}
                                    name="phone"
                                    label={t("clients.AddConnectedCompanyPerson.phone")}
                                    props={{
                                        fullWidth: true,
                                        size: "small",
                                        error: !!formState.errors.phone,
                                        helperText: formState.errors.phone?.message,
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container justifyContent={"center"}>
                            <Grid item xs={6}>
                                <ControlledTextField
                                    control={control}
                                    name="email"
                                    label={t("clients.AddConnectedCompanyPerson.email")}
                                    props={{
                                        fullWidth: true,
                                        size: "small",
                                        error: !!formState.errors.email,
                                        helperText: formState.errors.email?.message,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid xs={6} item sx={{ mx: "auto", mt: 3 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!formState.isValid}
                            onClick={() => {
                                handleSubmit(props.onSubmit)();
                            }}
                        >
                            {t("clients.AddConnectedCompanyPerson.add")}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
